import { registerApplication, start } from "single-spa";
import { constructApplications, constructLayoutEngine, matchRoute } from "single-spa-layout";
import { createAnalyticsInterface } from "@gwn/guac-lib";
import { getConfiguration, getRoutes, getImportMap } from "./drs/services/drsService";
import { createNavigation } from "./drs/navigation";
import { createRouting } from "./drs/routing";
import { DRSApplication } from "./types/drs";
import { removeLoadingScreen } from "./helpers/loading";
import { checkUserLaunchPoint } from "./helpers/homeRouteFallback";
import { apiInterface } from "./api/apiInterface";
import { configInterface, sentry } from "./helpers/configInterface";
import { getAuthorizationInterface } from "./static/authorization";
import { SHARED_IMPORTS, NEWEST_SHARED_DEPS_CONFIG } from "./constants/sharedDepsConfig";
import { makeMain } from "@sentry/browser";

const measurementId = process.env.GUAC_GA_MEASUREMENT_ID;
const measurementServiceName = measurementId ? "googleAnalytics" : "disabled";
let loadedConf = undefined;
let drsConfiguration = undefined;
let importMap = undefined;

export const loadSharedLibraries = async (version) => {
  loadedConf = version;
  System.addImportMap(importMap);
  System.addImportMap(SHARED_IMPORTS[version].scripts);
  // @ts-ignore
  System.addImportMap(importMapOverrides.getOverrideMap());
  loadSharedStyles(SHARED_IMPORTS[version].styles);
  await loadSharedScripts();
};

export const loadSharedScripts = async () => {
  await System.import("vue");
  // @ts-ignore
  System.set(System.resolve("vue"), window.Vue);
  await Promise.all([
    System.import("vue-demi"),
    System.import("vuetify"),
    System.import("vue-router"),
    System.import("vue-i18n"),
  ]);
  await System.import("pinia");
  // @ts-ignore
  System.set(System.resolve("pinia"), window.Pinia);

  await System.import("@getwell/guac-layout");
};

const loadSharedStyles = (styles) => {
  styles.forEach((style) => {
    document
      .getElementsByTagName("head")[0]
      .insertAdjacentHTML("beforeend", `<link rel="stylesheet" href="${style}" />`);
  });
};

const addRoutingListener = () => {
  window.addEventListener("single-spa:before-routing-event", handleBeforeRoutingEvent);
};

export const handleBeforeRoutingEvent = (event: CustomEvent) => {
  if (event.detail.totalAppChanges > 0 && sentry) {
    // re-enable sentry for guac
    makeMain(sentry);
  }
  const app = event.detail.appsByNewStatus.MOUNTED.filter((app) => !(app === "@getwell/guac-layout"))[0];
  if (!app) {
    return;
  }
  const newConfig = getActiveConfigVersion(app);
  if (newConfig && newConfig !== loadedConf) {
    event.detail.cancelNavigation();
    window.location.href = event.detail.newUrl;
  }
};

export const getActiveConfigVersion = (activeApplication) => {
  let newConfig = undefined;
  drsConfiguration.applications.find((application) => {
    if (activeApplication !== application.identifier) {
      return;
    }
    const sharedDeps = SHARED_IMPORTS[application.shared_libraries_version];
    const applicationSlv = sharedDeps ? application.shared_libraries_version : "legacy";
    if (!loadedConf) {
      newConfig = applicationSlv;
    } else if (loadedConf !== applicationSlv) {
      newConfig = applicationSlv;
    }

    return application;
  });

  // fallback to load guac-layout
  if (!loadedConf && !newConfig) {
    newConfig = NEWEST_SHARED_DEPS_CONFIG;
  }

  return newConfig;
};

export const sharedDependenciesGuard = async (activeApplication) => {
  const newConfig = getActiveConfigVersion(activeApplication);
  if (!loadedConf && newConfig) {
    await loadSharedLibraries(newConfig);
  }
  return newConfig;
};

export const getApplicationFromActiveRoutes = (activeRoutes) => {
  for (const route of activeRoutes.routes) {
    const activeApplications = route.routes.reduce((apps, application) => {
      if (application.name !== "@getwell/guac-layout") {
        apps.push(application.name);
      }
      return apps;
    }, []);

    if (activeApplications.length) {
      return activeApplications[0];
    }
  }
};

/**
 * Creates layout, mounts microfrontends and parcels.
 */
export const initSingleSpa = async () => {
  try {
    const analytics = createAnalyticsInterface({
      applicationName: "guac-core",
      serviceName: measurementServiceName,
      trackingId: measurementId,
    });

    const config = await Promise.all([getImportMap(), getConfiguration(), getRoutes()]);
    importMap = config[0];
    drsConfiguration = config[1];
    const drsRoutes = config[2];

    addRoutingListener();

    const routing = createRouting(drsRoutes);
    const path = window.location.pathname;

    const activeRoutes = matchRoute(routing, path);

    const activeApplication = getApplicationFromActiveRoutes(activeRoutes);

    await sharedDependenciesGuard(activeApplication);

    const navOptions = createNavigation(drsRoutes);

    checkUserLaunchPoint(routing, drsRoutes);

    const customProps = {
      interfaces: {
        navOptions,
        authInterface: getAuthorizationInterface(),
        apiInterface,
        configInterface,
      },
    };

    const applications = constructApplications({
      routes: routing,
      loadApp: ({ name }) => {
        return System.import(name);
      },
    });

    const layoutEngine = constructLayoutEngine({
      routes: routing,
      applications,
      active: false,
    });

    applications.forEach((application) => {
      configureApplication(application, drsConfiguration.applications, customProps);

      registerApplication(application);
    });

    layoutEngine.activate();

    start({
      urlRerouteOnly: true,
    });

    removeLoadingScreen();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log("initSingleSpa", err);
  }
};

/**
 * Assign a props to the application, based on app type and avaiable interfaces
 */
export const configureApplication = (application, drsApplications: DRSApplication[], customProps) => {
  const availableInterfacesNames = getAvailableInterfaceNames(application.name, drsApplications);
  application.customProps = {
    interfaces: {},
  };

  availableInterfacesNames.forEach((interfaceName) => {
    if (interfaceName === "analyticsInterface") {
      const measurementId = process.env.GUAC_GA_MEASUREMENT_ID;

      application.customProps.interfaces[interfaceName] = createAnalyticsInterface({
        applicationName: application.name,
        serviceName: measurementServiceName,
        trackingId: measurementId,
      });

      return;
    }

    application.customProps.interfaces[interfaceName] = customProps.interfaces[interfaceName];
  });
};

export const getAvailableInterfaceNames = (applicationName, drsApplications: DRSApplication[]): string[] => {
  const application = drsApplications.find((app) => app.identifier === applicationName);

  return application?.available_interfaces || [];
};

// for test purposes only
export const destroy = () => {
  loadedConf = undefined;
  drsConfiguration = undefined;
  window.removeEventListener("single-spa:before-routing-event", handleBeforeRoutingEvent);
};
